import { Alert, Box, MenuItem, Paper, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCallback, useMemo } from 'react'

import { useBookingContext } from '@providers/booking'
import { i18WithDefault as t } from '@shared/locale'
import { getDOWFromStr } from 'components-ui/src/shared/dates'
import { checkInOutTimeIntervals, isCheckInTimeEnabled, isCheckOutTimeEnabled } from 'components-ui/src/shared/location'
import { getOrderPrimaryServiceTypeName } from 'components-ui/src/shared/orders'
import {
  displayHoursInPeriodRestrictions,
  getClockBoundsForDayOfWeek,
  isSelectedDateInBookingRestrictions,
  isSelectedDateInPeriodRestrictionsRelation,
  isSelectedTimeInPeriodRestrictions,
} from 'components-ui/src/shared/restrictions'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { PaymentSettingAdminDto, RestrictionClientDto } from 'service-api/src/types/api'
import { TCheckInOutProps } from './details_policies.d'

export const CheckInOut = ({ formik, reservation, fieldRefs }: TCheckInOutProps) => {
  const { order, locationBookingConf } = reservation
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const isDaycare = getOrderPrimaryServiceTypeName(order) === 'daycare'

  const { startDate, endDate } = useMemo(() => {
    const invoice = order?.invoices?.find(({ items }) => {
      return items?.some(({ locationServiceTypeId }) => locationServiceTypeId === order?.primaryServiceType?.id)
    })
    const { startDate, endDate } = invoice?.period?.[0] || invoice?.requestPeriods?.[0] || {}
    if (bookingCtx.state.data?.serviceType?.serviceType?.overnight === false) {
      return {
        startDate,
        endDate: startDate,
      }
    }
    return { startDate, endDate }
  }, [order, bookingCtx.state.data?.serviceType])

  const checkInDay = startDate && getDOWFromStr(startDate)
  const checkOutDate = endDate
  const checkOutDay = checkOutDate && getDOWFromStr(checkOutDate)
  const isSurchargeStrategyEnabled =
    locationBookingConf?.surchargeStrategy === 'ENFORCED' || locationBookingConf?.surchargeStrategy === 'DURATION'

  const isCheckInEnabled =
    !!startDate &&
    isSelectedDateInBookingRestrictions(reservation.checkInHour ?? [], startDate) &&
    locationBookingConf?.surchargeStrategy !== 'DISABLED'
  const checkInPeriod =
    isCheckInEnabled && !isSurchargeStrategyEnabled ? reservation.checkInHour : reservation.operationHour
  const checkInTimes = useMemo(
    () => checkInOutTimeIntervals(startDate, checkInPeriod as RestrictionClientDto[]),
    [startDate, checkInPeriod]
  )

  const isCheckOutEnabled =
    !!checkOutDate &&
    isSelectedDateInBookingRestrictions(reservation.checkOutHour ?? [], checkOutDate) &&
    locationBookingConf?.surchargeStrategy !== 'DISABLED'
  const checkOutPeriod =
    isCheckOutEnabled && !isSurchargeStrategyEnabled ? reservation.checkOutHour : reservation.operationHour
  const checkOutTimes = useMemo(
    () => checkInOutTimeIntervals(checkOutDate, checkOutPeriod as RestrictionClientDto[]),
    [checkOutDate, checkOutPeriod]
  )

  const hasCheckInHourTimesForDay = useMemo(() => {
    if (!checkInDay || !reservation.checkInHour?.length) {
      return false
    }
    return displayHoursInPeriodRestrictions(reservation.checkInHour, checkInDay).trim().length > 0
  }, [checkInDay, reservation.checkInHour])

  const hasCheckOutHourTimesForDay = useMemo(() => {
    if (!checkOutDay || !reservation.checkOutHour?.length) {
      return false
    }
    return displayHoursInPeriodRestrictions(reservation.checkOutHour, checkOutDay).trim().length > 0
  }, [checkOutDay, reservation.checkOutHour])

  const checkInClockBounds = useMemo(() => {
    if (!checkInDay || !reservation.checkInHour?.length) {
      return null
    }
    return getClockBoundsForDayOfWeek(reservation.checkInHour, checkInDay)
  }, [checkInDay, reservation.checkInHour])

  const checkOutClockBounds = useMemo(() => {
    if (!checkOutDay || !reservation.checkOutHour?.length) {
      return null
    }
    return getClockBoundsForDayOfWeek(reservation.checkOutHour, checkOutDay)
  }, [checkOutDay, reservation.checkOutHour])

  const showCheckInFeeSuffix = (timez: string) => {
    if (
      locationBookingConf?.surchargeStrategy !== 'ENFORCED' ||
      !hasCheckInHourTimesForDay ||
      !checkInDay ||
      !checkInClockBounds
    ) {
      return false
    }
    const surchargeSettings =
      (order?.data as Record<string, PaymentSettingAdminDto[]> | undefined)?.surchargeSettings ?? []
    const hasFees = surchargeSettings.some(
      (fee) =>
        fee?.before &&
        startDate &&
        isSelectedDateInPeriodRestrictionsRelation(fee.restrictionRelations!, checkInDay, startDate)
    )
    return hasFees && timez < checkInClockBounds.minStart
  }

  const showCheckOutFeeSuffix = (timez: string) => {
    if (
      locationBookingConf?.surchargeStrategy === 'DISABLED' ||
      !hasCheckOutHourTimesForDay ||
      !checkOutDay ||
      !checkOutClockBounds ||
      !reservation.checkOutHour?.length
    ) {
      return false
    }
    const surchargeSettings =
      (order?.data as Record<string, PaymentSettingAdminDto[]> | undefined)?.surchargeSettings ?? []
    const hasFees = surchargeSettings.some(
      (fee) =>
        fee?.after &&
        endDate &&
        isSelectedDateInPeriodRestrictionsRelation(fee.restrictionRelations!, checkOutDay, endDate)
    )
    return (
      hasFees &&
      timez > checkOutClockBounds.maxEnd &&
      !isSelectedTimeInPeriodRestrictions(reservation.checkOutHour, checkOutDay, timez)
    )
  }

  const isCheckInSurchargeApplicable =
    !!formik.values.checkInTime && isCheckInEnabled && showCheckInFeeSuffix(formik.values.checkInTime)

  const isCheckOutSurchargeApplicable =
    !!formik.values.checkOutTime &&
    isCheckOutEnabled &&
    showCheckOutFeeSuffix(formik.values.checkOutTime) &&
    (locationBookingConf?.surchargeStrategy === 'ENFORCED' || locationBookingConf?.surchargeStrategy === 'DURATION')

  const isFeeWarningVisible = isCheckInSurchargeApplicable || isCheckOutSurchargeApplicable

  const isDaycareTimeRangeInvalid =
    isDaycare &&
    !!formik.values.checkInTime &&
    !!formik.values.checkOutTime &&
    formik.values.checkInTime > formik.values.checkOutTime

  const onChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const { name, id, value } = ev.target
      bookingCtx.dispatch({
        type: 'SET_CHECK_IN_OUT_FORM',
        payload: {
          value: {
            fieldName: name || id,
            value,
          },
        },
      })

      formik.handleChange(ev)
    },
    [bookingCtx, formik]
  )

  return (
    <Paper elevation={1} sx={{ p: 2, pb: 3, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
      <Typography variant="h5">{t('checkInOutForm.title')}</Typography>
      <Typography variant="body1" mt={3} mb={2}>
        {t('checkInOutForm.subtitle')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        {isCheckInTimeEnabled(reservation.locationBookingConf!) && (
          <TextField
            id="checkInTime"
            name="checkInTime"
            label={t('checkInOutForm.checkIn')}
            variant="outlined"
            size="small"
            select
            sx={{ flexBasis: 0, flexGrow: 1 }}
            value={formik.values.checkInTime}
            onChange={onChange}
            error={formik.touched.checkInTime && Boolean(formik.errors.checkInTime)}
            helperText={formik.touched.checkInTime && formik.errors.checkInTime}
            inputRef={(el) => fieldRefs && (fieldRefs.current.checkInTime = el)}
          >
            {checkInTimes.map((ctime, i) => (
              <MenuItem key={`${ctime.ampm}-${i}`} value={ctime.timez}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                  <span>{ctime.ampm}</span>
                  {showCheckInFeeSuffix(ctime.timez) && (
                    <Typography component="span" variant="body2" sx={{ color: 'warning.main' }}>
                      {t('checkInOutForm.feeSuffix')}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        )}
        {isCheckOutTimeEnabled(reservation.locationBookingConf!) && (
          <TextField
            id="checkOutTime"
            name="checkOutTime"
            label={t('checkInOutForm.checkOut')}
            variant="outlined"
            size="small"
            select
            sx={{ flexBasis: 0, flexGrow: 1 }}
            value={formik.values.checkOutTime}
            onChange={onChange}
            error={formik.touched.checkOutTime && Boolean(formik.errors.checkOutTime)}
            helperText={formik.touched.checkOutTime && formik.errors.checkOutTime}
            inputRef={(el) => fieldRefs && (fieldRefs.current.checkOutTime = el)}
          >
            {checkOutTimes.map((ctime, i) => (
              <MenuItem key={`${ctime.ampm}-${i}`} value={ctime.timez}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                  <span>{ctime.ampm}</span>
                  {showCheckOutFeeSuffix(ctime.timez) && (
                    <Typography component="span" variant="body2" sx={{ color: 'warning.main' }}>
                      {t('checkInOutForm.feeSuffix')}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      <Stack>
        {(isFeeWarningVisible || isDaycareTimeRangeInvalid) && (
          <>
            {isDaycareTimeRangeInvalid ? (
              <Alert severity="error">
                <Typography variant="body2">{t('checkInOutForm.error')}</Typography>
              </Alert>
            ) : (
              <Alert severity="warning">
                <Typography variant="body2">{t('checkInOutForm.warning')}</Typography>
              </Alert>
            )}
            <Spacer height={1} />
          </>
        )}
        {locationBookingConf?.surchargeStrategy === 'DURATION' && !isDaycare ? (
          <Typography variant="body2" color="text.secondaryShade70">
            {t('checkInOutForm.duration')}
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {isCheckInEnabled && checkInDay && (
              <Typography variant="body2" color="text.secondaryShade70">
                {t('checkInOutForm.checkInTimes')}:{' '}
                {displayHoursInPeriodRestrictions(reservation.checkInHour!, checkInDay)}
              </Typography>
            )}
            {isCheckOutEnabled && checkOutDay && (
              <Typography variant="body2" color="text.secondaryShade70">
                {t('checkInOutForm.checkOutTimes')}:{' '}
                {displayHoursInPeriodRestrictions(reservation.checkOutHour!, checkOutDay)}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}
